// The list of keyboard shortcuts (`?`), drawn from the registry only.
import { m } from "@trameria/i18n";
import { useSyncExternalStore } from "react";
import { useMeApp } from "../app/context";
import { Modal } from "../ui/modal";
import { keyLabel, type ShortcutGroup, useShortcutList } from "./shortcuts";

export interface SheetState {
  isOpen(): boolean;
  open(): void;
  close(): void;
  toggle(): void;
  subscribe(fn: () => void): () => void;
}

export function createSheetState(): SheetState {
  let value = false;
  const subs = new Set<() => void>();
  const set = (v: boolean) => {
    value = v;
    for (const fn of subs) fn();
  };
  return {
    isOpen: () => value,
    open: () => set(true),
    close: () => set(false),
    toggle: () => set(!value),
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}

export function useShortcutSheet() {
  const { sheet } = useMeApp();
  const isOpen = useSyncExternalStore(
    sheet.subscribe,
    sheet.isOpen,
    sheet.isOpen,
  );
  return { isOpen, open: sheet.open, close: sheet.close, toggle: sheet.toggle };
}

const GROUPS: ShortcutGroup[] = ["general", "navigate", "create", "view"];
const groupLabel = (g: ShortcutGroup) =>
  ({
    general: m.shell_shortcuts_group_general,
    navigate: m.shell_shortcuts_group_navigate,
    create: m.shell_shortcuts_group_create,
    view: m.shell_shortcuts_group_view,
  })[g]();

export function ShortcutSheet() {
  const sheet = useShortcutSheet();
  const list = useShortcutList().filter((d) => d.when?.() ?? true);
  return (
    <Modal
      open={sheet.isOpen}
      onClose={sheet.close}
      label={m.shell_shortcuts()}
    >
      <div className="text-[15px] text-fg">{m.shell_shortcuts()}</div>
      <div className="mt-3 max-h-[60vh] overflow-y-auto">
        {GROUPS.map((g) => {
          // Two keys for the same thing (`?` and `mod+/`) share one row.
          const rows: { id: string; label: string; keys: string[] }[] = [];
          for (const d of list) {
            if (d.group !== g) continue;
            const label = d.label();
            const same = rows.find((r) => r.label === label);
            if (same) same.keys.push(...keyLabel(d.key));
            else rows.push({ id: d.id, label, keys: keyLabel(d.key) });
          }
          if (rows.length === 0) return null;
          return (
            <section key={g} className="mt-3 first:mt-0">
              <div className="text-[11px] tracking-[0.04em] text-faint">
                {groupLabel(g)}
              </div>
              {rows.map((d) => (
                <div
                  key={d.id}
                  className="flex h-8 items-center justify-between text-[13px] text-body"
                >
                  <span>{d.label}</span>
                  <span className="flex gap-1">
                    {d.keys.map((k) => (
                      <kbd
                        key={k}
                        className="inline-flex h-5 min-w-5 items-center justify-center rounded-[4px] border border-border px-1 font-sans text-[11px] text-muted"
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </Modal>
  );
}
